import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { type ApiError } from '@/lib/api/client';
import { createCarrierConnection } from '@/lib/api/carrier-connections';

/**
 * Modal that takes a Royal Mail Click & Drop API token, validates it
 * via `POST /carrier-connections` (the handler proxies to the C&D API
 * before persisting), and surfaces the result.
 *
 * Token generation isn't a single menu path — C&D's settings UI is a
 * SPA whose URL doesn't change as the merchant navigates. The visible
 * step-by-step here tells them where to start; the rest is a single
 * click trail inside the C&D admin.
 */
interface RoyalMailCdConnectDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /**
   * Fires after a successful 201 from `POST /carrier-connections`.
   * The created row is intentionally NOT passed back — Step 4 reads
   * the canonical list by invalidating the tenant cache and re-
   * fetching, so passing the summary here would create a second
   * source of truth.
   */
  readonly onConnected: () => void;
}

export function RoyalMailCdConnectDialog({
  open,
  onClose,
  onConnected,
}: RoyalMailCdConnectDialogProps) {
  const [apiToken, setApiToken] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setApiToken('');
    setDisplayName('');
    setError(null);
    setSubmitting(false);
  }

  function handleOpenChange(next: boolean) {
    if (next || submitting) return;
    reset();
    onClose();
  }

  async function submit() {
    setError(null);
    if (apiToken.trim().length === 0) {
      setError('Paste your Click & Drop API token to continue.');
      return;
    }
    setSubmitting(true);
    const trimmedDisplayName = displayName.trim();
    const result = await createCarrierConnection({
      kind: 'royalmail_cd',
      ...(trimmedDisplayName.length > 0 && { display_name: trimmedDisplayName }),
      credentials: { apiToken: apiToken.trim() },
    });
    setSubmitting(false);
    if (result.isErr()) {
      setError(mapErrorToMessage(result.error));
      return;
    }
    reset();
    onConnected();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent showCloseButton={!submitting} className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Connect Royal Mail Click & Drop</DialogTitle>
          <DialogDescription>
            Use a personal API token from your Click & Drop account.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <ol className="text-muted-foreground list-decimal space-y-1 pl-5 text-sm">
            <li>
              Open{' '}
              <a
                href="https://business.parcel.royalmail.com/settings/channels/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline-offset-2 hover:underline"
              >
                business.parcel.royalmail.com → Settings → Channels
              </a>
              .
            </li>
            <li>
              Click <span className="text-foreground">Add new integration</span>, then choose API.
            </li>
            <li>Copy the generated token and paste it below.</li>
          </ol>

          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="cd-token">API token</Label>
            <Input
              id="cd-token"
              type="password"
              autoComplete="off"
              spellCheck={false}
              placeholder="ey…"
              value={apiToken}
              onChange={(e) => {
                setApiToken(e.target.value);
              }}
              disabled={submitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cd-display-name">Account label (optional)</Label>
            <Input
              id="cd-display-name"
              placeholder="Royal Mail Click & Drop"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
              }}
              maxLength={200}
              disabled={submitting}
            />
            <p className="text-muted-foreground text-xs">
              Helps when you have more than one C&D account on this tenant.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={submitting}
            onClick={() => {
              handleOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={submitting}
            onClick={() => {
              void submit();
            }}
          >
            {submitting ? 'Connecting…' : 'Connect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function mapErrorToMessage(error: ApiError): string {
  // 401 from the handler can mean either the Cognito session expired
  // (no/expired Bearer header) OR the carrier rejected the token
  // (invalid_carrier_credentials body). Both are 401 client-side;
  // disambiguate by message body.
  if (error.kind === 'unauthorised') {
    if (error.message.includes('invalid_carrier_credentials')) {
      return "Royal Mail didn't recognise that token. Generate a fresh one and try again.";
    }
    return 'Your session expired. Please sign in again.';
  }
  // 502 from the handler when C&D is unreachable; mapped to server_error
  // client-side because >= 500.
  if (error.kind === 'server_error' && error.message.includes('carrier_unreachable')) {
    return "We couldn't reach Royal Mail just now. Please retry in a moment.";
  }
  return "We couldn't save the connection. Please try again.";
}
